import React, { useEffect } from 'react';

import {
    SetServiceLaunchDateInput,
    SetServiceLaunchDateOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isEqual } from 'lodash';
import { useLocation } from 'react-router';
import { Route, Switch, useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { StepTracker } from 'src/components/StepTracker';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { defaultLaunchDateValues } from 'src/constants/launchDates';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IAddLaunchDateProps } from 'src/types/ILaunchDates';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';

import './AddLaunchDate.scss';
import { InputLaunchDate } from './InputLaunchDate';
import { ReviewLaunchDate } from './ReviewLaunchDate';

export const AddLaunchDate = ({ setIsDataLoaded, existingLaunchID, setLaunchDateToEdit }: IAddLaunchDateProps) => {
    const history = useHistory();

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.SERVICE_LAUNCHES.INDEX, title: 'Launch Dates' },
        { link: '', title: existingLaunchID ? 'Edit launch date' : 'Add launch date' }
    ];

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.SERVICE_LAUNCHES.STEP.INPUT,
            stepTitle: 'Input Launch',
            StepComponent: InputLaunchDate
        },
        {
            stepId: ROUTES.SERVICE_LAUNCHES.STEP.REVIEW,
            stepTitle: 'Review',
            StepComponent: ReviewLaunchDate
        }
    ];

    const ADD_LAUNCH_DATE_BASE_PATH = `${ROUTES.SERVICE_LAUNCHES.INDEX}${ROUTES.SERVICE_LAUNCHES.ADD}`;

    const { pathname } = useLocation();
    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const { setToasts, userInfo, setTopAlertMsg } = React.useContext(AppContext);
    const [isCancelModalOpen, setIsCancelModalOpen] = React.useState<boolean>(false);
    const [launchDateValues, setLaunchDateValues] = React.useState(defaultLaunchDateValues);

    useEffect(() => {
        if (existingLaunchID) {
            setLaunchDateValues((prevState) => ({
                ...prevState,
                launchToBeDeactivated: existingLaunchID
            }));
        }
    }, []);

    const handleLaunchDateValuesChange = (key: string, value: string | boolean) => {
        setLaunchDateValues((prevState) => ({
            ...prevState,
            [key]: value
        }));
    };

    const onNext = () => {
        history.push(`${ADD_LAUNCH_DATE_BASE_PATH}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${ADD_LAUNCH_DATE_BASE_PATH}${steps[currentStepIndex - 1].stepId}`);
    };

    const onSubmit = () => {
        contractConfigurationClient
            .setServiceLaunchDate(
                SetServiceLaunchDateInput.clone({
                    serviceID: parseInt(launchDateValues.serviceID),
                    territoryCode: launchDateValues.territoryCode,
                    launchDate: launchDateValues.launchDate?.replaceAll('-', ''),
                    endDate: launchDateValues.endDate
                        ? launchDateValues.endDate.replaceAll('-', '')
                        : launchDateValues.endDate,
                    changedBy: userInfo.username,
                    existingLaunchDateID:
                        launchDateValues.launchToBeDeactivated !== ''
                            ? parseInt(launchDateValues.launchToBeDeactivated as string)
                            : undefined
                })
            )
            .subscribe(
                (response: SetServiceLaunchDateOutput) => {
                    if (existingLaunchID !== undefined) {
                        var toast: IToast = {
                            id: 'add-launch-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: '1 launch date edited'
                        };
                    } else {
                        var toast: IToast = {
                            id: 'add-launch-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: '1 new launch date added'
                        };
                    }
                    setToasts([toast]);
                    setIsDataLoaded(false);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('setServiceLaunchDate unexpected error', error.receivedException.message);
                    setTopAlertMsg('Error setting launch date with setServiceLaunchDate API');
                }
            );
        history.push(ROUTES.SERVICE_LAUNCHES.INDEX);
    };

    const onCancel = () => {
        if (!isEqual(launchDateValues, defaultLaunchDateValues)) {
            setIsCancelModalOpen(true);
        } else {
            history.push(ROUTES.SERVICE_LAUNCHES.INDEX);
        }
    };

    const handleBreadcrumbRedirect = () => {
        if (!isEqual(launchDateValues, defaultLaunchDateValues)) {
            setIsCancelModalOpen(true);
            return true;
        } else {
            return false;
        }
    };

    return (
        <Column alignmentHorizontal="center" spacingInset="500 500 600">
            <Column width={950} maxWidth="100%" spacing="500">
                <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} handleRedirect={handleBreadcrumbRedirect} />

                <Row alignmentVertical="top" wrap="down" widths={['fit', 'fill']}>
                    <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                        <StepTracker
                            steps={steps}
                            basePath={ADD_LAUNCH_DATE_BASE_PATH}
                            currentStepIndex={currentStepIndex}
                        />
                    </Column>
                    <Column spacing="300" maxWidth="550px">
                        <Switch>
                            {steps.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${ADD_LAUNCH_DATE_BASE_PATH}${stepId}`}>
                                    <StepComponent
                                        launchDateValues={launchDateValues}
                                        setLaunchDateValues={setLaunchDateValues}
                                        handleLaunchDateValuesChange={handleLaunchDateValuesChange}
                                        steps={steps}
                                        onCancel={onCancel}
                                        onPrevious={onPrevious}
                                        onNext={onNext}
                                        onSubmit={onSubmit}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Column>
                </Row>
            </Column>

            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title={!existingLaunchID ? 'Cancel add launch date' : 'Cancel edit launch date'}
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText={existingLaunchID == undefined ? 'Cancel add launch' : 'Cancel edit launch'}
                primaryButtonOnClick={() => {
                    history.push(ROUTES.SERVICE_LAUNCHES.INDEX);
                    if (launchDateValues.launchToBeDeactivated) {
                        setLaunchDateValues((prevState) => ({
                            ...prevState,
                            launchToBeDeactivated: ''
                        }));
                        setLaunchDateToEdit(null);
                    }
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
        </Column>
    );
};
